import ClientBase from "Services/API/ClientBase";

export const enum MFAMethod {
   TOTP = "TOTP",
   SMS = "SMS",
   EMAIL = "EMAIL",
   //not available for .net <8
   FIDO2 = "FIDO2",
   // LEGACY! TO BE REMOVED AFTER MIGRATION
   Authy = "Authy",
}

export interface LogonSuccessResponseGeneric {
   success: true,
}

export interface LogonSuccessResponseNormal extends LogonSuccessResponseGeneric {
   showTwoFactorConfirmation?: false,

   userId: Guid,
   firstName: string,
   fullName: string,
   showTermsOfUse?: boolean,
}

export interface LogonSuccessResponseTwoFactorConfirmationRequired extends LogonSuccessResponseGeneric {
   showTwoFactorConfirmation: true,

   phoneNumberLast4: string,
   mfaMethods: MFAMethod[],
}

export type LogonSuccessResponse = LogonSuccessResponseNormal | LogonSuccessResponseTwoFactorConfirmationRequired;

export interface LogonErrorResponse {
   success: false,
   error: string,
   logonStatus?: LSCommon.Enums.LogonStatusEnum,
   passwordValid?: boolean
}

export type LogonResponse = LogonErrorResponse | LogonSuccessResponse;

export type RegisterSuccessResponse = RegisterSuccessResponseNormal | RegisterSuccessResponseTwoFactorConfirmationRequired;

export interface RegisterSuccessResponseGeneric {
   success: true
}

export interface RegisterSuccessResponseNormal extends RegisterSuccessResponseGeneric {
   returnUrl?: string,
   userId: Guid,
   fullName: string,
   emailVerified: boolean

   showTwoFactorForm?: false
}

export interface RegisterSuccessResponseTwoFactorConfirmationRequired extends RegisterSuccessResponseGeneric {
   showTwoFactorForm: true,
   data: LSWebUI.Models.TwoFactorConfirmModel,
}

export interface RegisterErrorResponse {
   success: false,
   errors: string[],
   switchToLogin?: boolean
}

export type RegisterResponse = RegisterErrorResponse | RegisterSuccessResponse;

export type TwoFactorSuccessResponse = LogonSuccessResponse;

export interface TwoFactorErrorResponse {
   success: false,
   error: string
}

export type TwoFactorResponse = TwoFactorSuccessResponse | TwoFactorErrorResponse;

export interface TermOfUseHtmlResponse {
   termOfUseHtml: string
}

export interface ChangePasswordSuccessShowTermsOfUseResponse {
   success: true,
   showTermsOfUse: true,
   memberId: Guid,
}

export const isChangePasswordSuccessResponse = (response: ChangePasswordSuccessResponse): response is ChangePasswordSuccessShowTermsOfUseResponse => {
   return (response as ChangePasswordSuccessShowTermsOfUseResponse).showTermsOfUse === true
      && (response as ChangePasswordSuccessShowTermsOfUseResponse).memberId != undefined;
}

export type ChangePasswordSuccessResponse = ChangePasswordSuccessShowTermsOfUseResponse | LogonSuccessResponseNormal;

export interface ChangePasswordErrorResponse {
   success: false,
   errors: string[],
}

export type ChangePasswordResponse = ChangePasswordSuccessResponse | ChangePasswordErrorResponse;

export interface ChangeEmailSuccessResponse {
   success: true,
   returnUrl?: string,
}

export interface ChangeEmailErrorResponse {
   success: false,
   errors: string[],
}

export type ChangeEmailResponse = ChangeEmailSuccessResponse | ChangeEmailErrorResponse;

export class AccountClient extends ClientBase {
   protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

   constructor() {
      super();
   }

   async logon(logonRequest: Partial<LSCommon.Models.LogOnModelAnnotated>, signal?: AbortSignal | undefined): Promise<LogonResponse> {
      const url_ = "/Account/LogOn";

      const request = { ...logonRequest };
      request.IsDialog = true;

      const content_ = JSON.stringify(request);

      const options_ = <RequestInit>{
         body: content_,
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<LogonResponse>);
      });
   }

   async register(registerRequest: Partial<LSWebUI.Models.RegisterModel>, signal?: AbortSignal | undefined): Promise<RegisterResponse> {
      const url_ = "/Account/Register";

      const content_ = JSON.stringify(registerRequest);

      const options_ = <RequestInit>{
         body: content_,
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<RegisterResponse>);
      });
   }


   async adfsProviders(signal?: AbortSignal | undefined): Promise<LSCommon.Context.AdfsProvider[]> {
      const url_ = "/account/adfsProviders";

      const options_ = <RequestInit>{
         method: "GET",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<LSCommon.Context.AdfsProvider[]>);
      });
   }

   async termsOfUseHtml(signal?: AbortSignal | undefined): Promise<TermOfUseHtmlResponse> {
      const url_ = "/Account/TermOfUseHtml";

      const options_ = <RequestInit>{
         method: "GET",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<TermOfUseHtmlResponse>);
      });
   }


   async forgotPassword(changePasswordRequest: LSWebUI.Models.ForgotPasswordViewModel, signal?: AbortSignal | undefined): Promise<ChangePasswordResponse> {
      const url_ = "/Account/ForgotPasswordShowMessage";

      const content_ = JSON.stringify(changePasswordRequest);

      const options_ = <RequestInit>{
         body: content_,
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.json() as Promise<ChangePasswordResponse>);
      });
   }
   
   async newScimClientToken(accountId: string, signal?: AbortSignal | undefined): Promise<string> {
      const url_ = "/Organization/NewEnterpriseToken";

      const content_ = JSON.stringify({
         accountId: accountId
      });

      const options_ = <RequestInit>{
         body: content_,
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "text/html",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.text());
      });
   }

   async newScimClientSecret(signal?: AbortSignal | undefined): Promise<string> {
      const url_ = "/Organization/NewClientSecret";

      const options_ = <RequestInit>{
         method: "POST",
         signal,
         headers: {
            "Content-Type": "application/json",
            "Accept": "text/html",
            "X-Requested-With": "XMLHttpRequest", //required for .Net IsAjaxRequest() == true
         }
      };

      return fetch(url_, options_).then((_response: Response) => {
         return this.transformResult(url_, _response, (_response: Response) => _response.text());
      });
   }
}

export default AccountClient;